<template>
    <div class="folder-sidebar" :class="{ 'folder-sidebar--collapsed': isCollapsed }">
        <div class="folder-sidebar__header" v-if="!isCollapsed">
            <strong class="folder-sidebar__title">{{ $t('title') }}</strong>
            <button class="btn btn-sm btn-outline-secondary folder-sidebar__add-btn"
                    @click="showNewFolderForm = true"
                    v-b-tooltip.hover :title="$t('new_folder')">
                <i class="material-icons material-icons-round md-18">create_new_folder</i>
            </button>
        </div>

        <button class="btn btn-sm btn-outline-secondary folder-sidebar__collapse-btn"
                @click="isCollapsed = !isCollapsed"
                v-b-tooltip.hover :title="isCollapsed ? $t('expand_sidebar') : $t('collapse_sidebar')">
            <i class="material-icons material-icons-round md-18">
                {{ isCollapsed ? 'chevron_right' : 'chevron_left' }}
            </i>
        </button>

        <div class="folder-sidebar__content" v-if="!isCollapsed">
            <!-- All Invoices -->
            <div class="folder-sidebar__all"
                 :class="{ 'folder-sidebar__all--active': !activeFolderId }"
                 @click="setActiveFolder(null)">
                <i class="material-icons material-icons-round md-18 folder-sidebar__all-icon">receipt</i>
                <span>{{ $t('all_invoices') }}</span>
            </div>

            <!-- New Folder Form -->
            <div class="folder-sidebar__new-form" v-if="showNewFolderForm">
                <input class="form-control form-control-sm"
                       v-model="newFolderName"
                       ref="newFolderInput"
                       :placeholder="$t('folder_name')"
                       @keyup.enter="createFolder"
                       @keyup.escape="showNewFolderForm = false" />
                <select class="form-control form-control-sm mt-1" v-model="newFolderParent">
                    <option :value="null">{{ $t('root_level') }}</option>
                    <option v-for="folder in allFolders" :key="folder.id" :value="folder.id">
                        {{ folder.name }}
                    </option>
                </select>
                <div class="mt-1">
                    <button class="btn btn-sm btn-primary" @click="createFolder">{{ $t('create') }}</button>
                    <button class="btn btn-sm btn-link" @click="showNewFolderForm = false">{{ $t('cancel') }}</button>
                </div>
            </div>

            <!-- Folder Tree -->
            <div class="folder-sidebar__tree">
                <FolderTreeItem
                    v-for="folder in rootFolders"
                    :key="folder.id"
                    :folder="folder"
                    :all-folders="allFolders"
                    :active-folder-id="activeFolderId"
                    :invoice-counts="invoiceCounts"
                    @select="setActiveFolder"
                    @create-subfolder="openSubfolderForm"
                    @rename="renameFolder"
                    @delete="deleteFolder"
                />
            </div>

            <div class="folder-sidebar__empty" v-if="allFolders.length === 0">
                <small class="text-muted">{{ $t('no_folders') }}</small>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import FolderTreeItem from './FolderTreeItem';
import Folder from '@/store/models/folder';
import { VBTooltip } from 'bootstrap-vue';

export default {
    name: 'FolderSidebar',
    i18nOptions: { namespaces: 'folders' },
    components: { FolderTreeItem },
    directives: { 'b-tooltip': VBTooltip },
    props: {
        invoiceCounts: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            isCollapsed: false,
            showNewFolderForm: false,
            newFolderName: '',
            newFolderParent: null,
        };
    },
    computed: {
        ...mapGetters({
            activeFolderId: 'folders/activeFolderId',
            rootFolders: 'folders/roots',
        }),
        allFolders() {
            return Folder.query().where('$isNew', false).get();
        },
    },
    methods: {
        setActiveFolder(folderId) {
            this.$store.commit('folders/activeFolderId', folderId);
        },
        async createFolder() {
            if (!this.newFolderName.trim()) return;
            await this.$store.dispatch('folders/createFolder', {
                name: this.newFolderName.trim(),
                parent_id: this.newFolderParent,
            });
            this.newFolderName = '';
            this.newFolderParent = null;
            this.showNewFolderForm = false;
        },
        openSubfolderForm(parentId) {
            this.newFolderParent = parentId;
            this.newFolderName = '';
            this.showNewFolderForm = true;
            this.$nextTick(() => {
                if (this.$refs.newFolderInput) this.$refs.newFolderInput.focus();
            });
        },
        async renameFolder({ id, name }) {
            const folder = Folder.find(id);
            if (folder) {
                await this.$store.dispatch('folders/updateFolder', { ...folder, name });
            }
        },
        async deleteFolder(folderId) {
            if (confirm(this.$t('delete_confirm'))) {
                await this.$store.dispatch('folders/deleteFolder', folderId);
                if (this.activeFolderId === folderId) {
                    this.setActiveFolder(null);
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.folder-sidebar {
    width: 220px;
    min-width: 220px;
    border-right: 1px solid var(--divider);
    padding: 12px;
    background: var(--dp01);
    color: var(--text-primary);
    display: flex;
    flex-direction: column;
    position: relative;
    transition: width 0.2s, min-width 0.2s;

    &--collapsed {
        width: 40px;
        min-width: 40px;
        padding: 8px 4px;
        align-items: center;
    }

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
    }

    &__title {
        font-size: 14px;
        color: var(--text-primary);
    }

    &__add-btn {
        padding: 2px 6px;
    }

    &__collapse-btn {
        position: absolute;
        top: 8px;
        right: -16px;
        z-index: 1;
        padding: 2px 4px;
        border-radius: 50%;
        background: var(--dp04);
        color: var(--text-primary);
        box-shadow: 0 1px 3px var(--scrim);
    }

    &__content {
        flex: 1;
        overflow-y: auto;
    }

    &__all {
        display: flex;
        align-items: center;
        padding: 6px 8px;
        cursor: pointer;
        border-radius: 4px;
        font-size: 14px;
        margin-bottom: 4px;
        color: var(--text-primary);
        &:hover {
            background: var(--shade);
        }
        &--active {
            background: var(--divider);
            &:hover {
                background: var(--divider);
            }
        }
    }

    &__all-icon {
        margin-right: 6px;
        color: var(--text-secondary);
    }

    &__new-form {
        padding: 8px;
        background: var(--dp04);
        border: 1px solid var(--divider);
        border-radius: 4px;
        margin-bottom: 8px;
    }

    &__tree {
        margin-top: 4px;
    }

    &__empty {
        text-align: center;
        padding: 16px 0;
    }
}
</style>
